import React, { useState, useMemo } from 'react';
import SearchableSelect from './SearchableSelect';
import { COUNTRIES } from '../data/visa-data';

interface Destination {
  name: string;
  code: string;
  continent: string;
  type: 'visa_free' | 'e_visa' | 'visa_on_arrival' | 'visa_required';
  duration: string | null;
  raw: string;
}

interface ExplorerData {
  passport: { code: string; name: string };
  stats: { total: number; visa_free: number; e_visa: number; visa_on_arrival: number; visa_required: number };
  destinations: Destination[];
}

type Tab = 'all' | 'visa_free' | 'e_visa' | 'visa_on_arrival' | 'visa_required';

const TYPE_BADGE: Record<string, { label: string; className: string }> = {
  visa_free:      { label: 'Visa-Free',       className: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
  e_visa:         { label: 'eVisa',            className: 'bg-blue-50 text-blue-700 border border-blue-200' },
  visa_on_arrival:{ label: 'Visa on Arrival',  className: 'bg-amber-50 text-amber-700 border border-amber-200' },
  visa_required:  { label: 'Visa Required',    className: 'bg-rose-50 text-rose-700 border border-rose-200' },
};

const CONTINENT_COLORS: Record<string, string> = {
  'Europe':        'bg-violet-50 text-violet-600',
  'Asia':          'bg-orange-50 text-orange-600',
  'Africa':        'bg-yellow-50 text-yellow-700',
  'North America': 'bg-sky-50 text-sky-600',
  'South America': 'bg-teal-50 text-teal-600',
  'Oceania':       'bg-cyan-50 text-cyan-600',
  'Antarctica':    'bg-slate-100 text-slate-500',
};

function FlagImg({ code }: { code: string }) {
  if (!code) return <span className="w-6 h-4 rounded-sm bg-slate-200 inline-block" />;
  return (
    <img
      src={`https://flagcdn.com/24x18/${code.toLowerCase()}.png`}
      alt=""
      className="w-6 h-[18px] rounded-sm object-cover flex-shrink-0"
      onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
    />
  );
}

function StatCard({ value, label, color }: { value: number; label: string; color: string }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4 text-center shadow-sm">
      <p className={`text-2xl font-black ${color}`}>{value}</p>
      <p className="text-xs text-slate-500 mt-0.5 font-medium">{label}</p>
    </div>
  );
}

const TABS: { id: Tab; label: string }[] = [
  { id: 'all',             label: 'All' },
  { id: 'visa_free',       label: 'Visa-Free' },
  { id: 'e_visa',          label: 'eVisa' },
  { id: 'visa_on_arrival', label: 'Visa on Arrival' },
  { id: 'visa_required',   label: 'Visa Required' },
];

export default function PassportExplorer() {
  const [passportCode, setPassportCode] = useState('');
  const [data, setData] = useState<ExplorerData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('all');
  const [search, setSearch] = useState('');

  async function loadExplorer(code: string) {
    if (!code) return;
    setLoading(true);
    setError(null);
    setData(null);
    setActiveTab('all');
    setSearch('');
    try {
      const res = await fetch('/api/passport-explorer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passport: code }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to load passport data.');
      }
      setData(await res.json());
    } catch (e: any) {
      setError(e.message ?? 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  function handlePassportChange(code: string) {
    setPassportCode(code);
    if (code) loadExplorer(code);
  }

  const filtered = useMemo(() => {
    if (!data) return [];
    let list = data.destinations;
    if (activeTab !== 'all') list = list.filter(d => d.type === activeTab);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(d => d.name.toLowerCase().includes(q) || d.continent.toLowerCase().includes(q));
    }
    return list;
  }, [data, activeTab, search]);

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      {/* Passport selector */}
      <div className="mb-8">
        <h2 className="text-2xl font-black text-slate-900 mb-1">Passport Explorer</h2>
        <p className="text-slate-500 text-sm mb-5">Select your passport to see where you can go and what's required.</p>
        <div className="max-w-sm">
          <SearchableSelect
            label="Your Passport"
            options={COUNTRIES}
            value={passportCode}
            onChange={handlePassportChange}
            placeholder="Select passport country"
          />
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-24 gap-3 text-slate-500">
          <svg className="w-5 h-5 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          <span className="text-sm font-medium">Loading destinations…</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-2xl bg-rose-50 border border-rose-200 p-5 text-rose-700 text-sm font-medium">
          {error}
        </div>
      )}

      {/* Results */}
      {data && !loading && (
        <>
          {/* Passport headline */}
          <div className="flex items-center gap-3 mb-6">
            <FlagImg code={data.passport.code} />
            <div>
              <h3 className="font-black text-slate-900 text-lg leading-tight">{data.passport.name}</h3>
              <p className="text-slate-400 text-xs">{data.stats.total} destinations tracked</p>
            </div>
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-7">
            <StatCard value={data.stats.visa_free}       label="Visa-Free"        color="text-emerald-600" />
            <StatCard value={data.stats.e_visa}          label="eVisa"            color="text-blue-600" />
            <StatCard value={data.stats.visa_on_arrival} label="Visa on Arrival"  color="text-amber-600" />
            <StatCard value={data.stats.visa_required}   label="Visa Required"    color="text-rose-600" />
          </div>

          {/* Tabs + search */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
            <div className="flex gap-1 bg-slate-100 rounded-xl p-1 flex-wrap">
              {TABS.map(t => (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    activeTab === t.id
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {t.label}
                  {t.id === 'all'
                    ? ` (${data.stats.total})`
                    : (data.stats as any)[t.id] > 0
                      ? ` (${(data.stats as any)[t.id]})`
                      : ''}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Filter countries…"
              className="sm:ml-auto border border-slate-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 w-full sm:w-52"
            />
          </div>

          {/* Country list */}
          {filtered.length === 0 ? (
            <p className="text-slate-400 text-sm text-center py-12">No countries match your filter.</p>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              {filtered.map((dest, i) => {
                const badge = TYPE_BADGE[dest.type];
                const contColor = CONTINENT_COLORS[dest.continent] ?? 'bg-slate-100 text-slate-500';
                return (
                  <div
                    key={dest.code || dest.name}
                    className={`flex items-center gap-3 px-5 py-3.5 ${i < filtered.length - 1 ? 'border-b border-slate-50' : ''} hover:bg-slate-50 transition-colors`}
                  >
                    <FlagImg code={dest.code} />
                    <span className="flex-1 text-sm font-semibold text-slate-800 min-w-0 truncate">
                      {dest.name}
                    </span>
                    <span className={`hidden sm:inline-flex text-[10px] font-semibold px-2 py-0.5 rounded-full ${contColor}`}>
                      {dest.continent}
                    </span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {dest.duration && (
                        <span className="text-xs text-slate-400 font-medium">{dest.duration}</span>
                      )}
                      {badge && (
                        <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-lg ${badge.className}`}>
                          {badge.label}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Empty state */}
      {!loading && !error && !data && (
        <div className="text-center py-24 text-slate-400">
          <svg className="w-12 h-12 mx-auto mb-4 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2h1a2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
          </svg>
          <p className="text-sm font-medium">Select your passport to explore visa-free destinations</p>
        </div>
      )}
    </div>
  );
}
